import { z } from 'zod';
import { PendingRequestFlowSchema } from '../approval/pending_request_flow';
import {
  ApprovalRequestContextSchema,
  ApprovalVerdictSchema,
  NO_REQUEST_CONTEXT,
  OtherTripVoucherSchema,
  UNTRIAGED_VERDICT,
} from './approval_verdict';
import { VoucherViewSchema } from './traveler_view';

/**
 * Canonical wire contract for the pending-approvals read model: the approver's
 * vouchers awaiting a decision, grouped TRAVELER-PRIMARY (UX-01 / D-01) —
 * `travelers[] { trips[] { vouchers[] }, looseVouchers[] }`. Produced by the
 * customer-api `GET /internal/customers/:id/pending-approvals`, passed through
 * the app-integration BFF, and consumed by the web-chat approvals page. Each
 * pending voucher appears EXACTLY ONCE — under a trip group when it rides a
 * materialized trip, otherwise the traveler's `looseVouchers[]`. A voucher that
 * does not resolve in the mirror stays fail-soft (`voucher: null`).
 *
 * The customer-api router keeps its own file-local copy (mirroring
 * `DossierResponseSchema`); the cross-service CONSUMERS share this one.
 */
export const PendingApprovalVoucherSchema = z.object({
  voucherId: z.string(),
  voucher: VoucherViewSchema.nullable(),
  /** Stitched in by the app-integration BFF from the voucher-events triage read.
   * Defaulted so every producer that does not compute it (customer-api, trip-api,
   * the agents port) stays valid — and defaults to ATTENTION, never OK. */
  verdict: ApprovalVerdictSchema.default(UNTRIAGED_VERDICT),
  /** Stitched in alongside the verdict, from the same triage read. Defaulted
   * empty for the producers that never see the mirror-only triage block. */
  requestContext: ApprovalRequestContextSchema.default(NO_REQUEST_CONTEXT),
  /** The SOLICITANTE (who asked) — stitched from the same triage read. Sibling
   * of the verdict, canonically distinct from the traveler. `.default(null)` so
   * every producer that never computes it stays valid; `null` = not recorded. */
  requester: z.object({ name: z.string().nullable() }).nullable().default(null),
  /** When this voucher's fare stops holding — raw ISO instant, stitched from the
   * same triage read (`triage.expiration`). Drives the queue's urgency axis and
   * the live countdown chip. `null` = the source registered no deadline. */
  expiresAt: z.string().nullable().default(null),
  /** Pricing benchmark facts from the triage read — numbers only, the screen
   * decides whether "below market" is worth saying. `null` = not computed. */
  benchmark: z
    .object({ savingTotal: z.number().nullable(), currency: z.string().nullable() })
    .nullable()
    .default(null),
  /** `false` only in the company view, when the viewer is not among this
   * voucher's `userApprovals` (an ADMIN looking at someone else's queue). The
   * screen reads it to offer the admin override instead of the plain decision.
   * Defaulted `true` so every legacy producer stays valid. */
  awaitsViewer: z.boolean().default(true),
});
export type PendingApprovalVoucher = z.infer<typeof PendingApprovalVoucherSchema>;

export const PendingApprovalTripGroupSchema = z.object({
  tripId: z.string(),
  tripName: z.string().nullable(),
  // Airport-local wall-clock first, UTC ISO fallback — set by the producer.
  dates: z.object({ startAt: z.string().nullable(), endAt: z.string().nullable() }).nullable(),
  vouchers: z.array(PendingApprovalVoucherSchema),
  /** The trip's OTHER vouchers — everything not awaiting this approver — as
   * read-only context so the approver sees the whole trip. Defaulted: only
   * trip-api resolves them. */
  otherVouchers: z.array(OtherTripVoucherSchema).default([]),
  /** Whole-trip total (pending + the others). `null` when the vouchers do not
   * resolve to a single currency — same rule as `deriveAppTotalAmount`. */
  totalAmount: z.number().nullable().default(null),
  currency: z.string().nullable().default(null),
});
export type PendingApprovalTripGroup = z.infer<typeof PendingApprovalTripGroupSchema>;

export const PendingApprovalTravelerGroupSchema = z.object({
  // Scrubbed traveler publicId; `null` = the voucher did not resolve (fail-soft bucket).
  travelerId: z.string().nullable(),
  name: z.string().nullable(),
  /** Traveler photo, stitched in by the BFF from the customer-api batch (same
   * contract as `RequestedTripView.traveler.photoUrl`): absent when the lane is
   * not configured, `null` when the viewer may not see it. Short-lived URL. */
  photoUrl: z.url().nullable().optional(),
  trips: z.array(PendingApprovalTripGroupSchema),
  looseVouchers: z.array(PendingApprovalVoucherSchema),
  /** Pedidos (request flows `REQ-…`) que esperam ESTE aprovador (#4416): saldo de
   * carteira e acesso à mobilidade, que nunca viram voucher. OPCIONAL e aditivo:
   * ausente num produtor antigo — o consumidor lê `requests ?? []`. */
  requests: z.array(PendingRequestFlowSchema).optional(),
});
export type PendingApprovalTravelerGroup = z.infer<typeof PendingApprovalTravelerGroupSchema>;

/**
 * Pagination envelope (F1/D1 — a tela não carrega a fila inteira). OPTIONAL and
 * additive: absent when the caller did not ask for a page (`?limit=`), so every
 * pre-paging consumer keeps parsing untouched. `nextCursor` is an OPAQUE keyset
 * token (urgency, travelerId) — pass it back verbatim, never inspect it.
 * `totals` are queue-wide (not page-wide): the screen's scoreboard needs the
 * whole-queue counts even when only one page is loaded.
 */
/** Which queue the response shows: the viewer's own approvals or the whole company's. */
export const PendingApprovalsViewSchema = z.enum(['mine', 'company']);
export type PendingApprovalsView = z.infer<typeof PendingApprovalsViewSchema>;

/** Queue-wide counts of one view (not page-wide). */
export const PendingApprovalsTotalsSchema = z.object({
  travelers: z.number().int(),
  vouchers: z.number().int(),
  /** Pedidos pendentes da fila inteira (#4416); ausente num produtor antigo. */
  requests: z.number().int().nonnegative().optional(),
});
export type PendingApprovalsTotals = z.infer<typeof PendingApprovalsTotalsSchema>;

export const PendingApprovalsPageSchema = z.object({
  nextCursor: z.string().nullable(),
  // OPTIONAL (F4/F5): the producer computes this only for the FIRST page (no
  // cursor) — a later page pays only for its own rows, not the two
  // whole-queue aggregates again. The consumer keeps the last NON-null value
  // seen across the loaded pages.
  totals: PendingApprovalsTotalsSchema.optional(),
  /** The effective view of this response. Absent on a legacy producer. */
  view: PendingApprovalsViewSchema.optional(),
  /** Totals of BOTH views — first page only, and only for a viewer who may see
   * the company queue. Its PRESENCE is the signal for the screen to show the
   * mine/company filters. */
  views: z
    .object({ mine: PendingApprovalsTotalsSchema, company: PendingApprovalsTotalsSchema })
    .optional(),
});
export type PendingApprovalsPage = z.infer<typeof PendingApprovalsPageSchema>;

export const PendingApprovalsResponseSchema = z.object({
  travelers: z.array(PendingApprovalTravelerGroupSchema),
  page: PendingApprovalsPageSchema.optional(),
});
export type PendingApprovalsResponse = z.infer<typeof PendingApprovalsResponseSchema>;
